import Box from '@mui/material/Box';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreateIcon from '@mui/icons-material/Create';
import FolderIcon from '@mui/icons-material/Folder';
import GetAppIcon from '@mui/icons-material/GetApp';
import RepeatIcon from '@mui/icons-material/Repeat';
import ShieldIcon from '@mui/icons-material/Shield';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import {
  getSecurityFimEventDetailHref,
  getSecurityNavItemPath,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';
import type { WazuhFimEventItem } from 'src/types/mindtime/wazuhFileMonitoring';
import type { Column, TableData } from 'src/types/table';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrKpiCard,
  EdrPageHeader,
  EdrSeverityPill,
  EdrStatusPill,
  EdrTruncatedText,
  edrMonoStyle,
  edrMutedStyle,
  type EdrToolbarFilter,
} from '../shared/edrPrimitives';
import {
  CHANGE_TYPE_COLORS,
  fimSeverityToLevel,
  formatFimRuleDetail,
  getFimEventId,
  normalizeFimKey,
  toFimTitle,
} from './fileMonitoringDetailModel';
import { useFileMonitoringData, useFileMonitoringExport } from './useFileMonitoringData';

const ChangeTag: FC<{ value: string | undefined }> = ({ value }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      color: EDR_COLORS.textBody,
    }}
  >
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: CHANGE_TYPE_COLORS[normalizeFimKey(value)] ?? EDR_COLORS.slate500,
      }}
    />
    {toFimTitle(value)}
  </span>
);

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    {
      name: 'File Integrity Monitoring',
      to: getSecurityNavItemPath('file-integrity-monitoring') ?? '',
    },
  ],
};

const CHANGE_TYPE_OPTIONS = [
  { label: 'Added', value: 'added' },
  { label: 'Modified', value: 'modified' },
  { label: 'Deleted', value: 'deleted' },
];

const SEVERITY_OPTIONS = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const RULE_LEVEL_OPTIONS = Array.from({ length: 15 }, (_, index) => {
  const level = String(index + 2);
  return { label: level, value: level };
});

const DATE_RANGE_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

const formatNumber = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const FileIntegrityMonitoring: FC = () => {
  const navigate = useNavigate();
  const { dateRange, setDateRange, isReseller } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.fileIntegrityMonitoring);
  const {
    changeType = '',
    severityBand = '',
    ruleLevel = '',
    wazuhAgentId = '',
    organizationId = '',
  } = filters;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fileFilters = useMemo(
    () => ({
      changeType: filters.changeType,
      severityBand: filters.severityBand,
      ruleLevel: filters.ruleLevel,
      wazuhAgentId: filters.wazuhAgentId,
      organizationId: filters.organizationId,
    }),
    [filters]
  );

  const openFimEventDetail = useCallback(
    (item: WazuhFimEventItem) => {
      const id = getFimEventId(item);
      if (!id) return;
      navigate(getSecurityFimEventDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const { loading, summary, items: paged } = useFileMonitoringData(
    page,
    rowsPerPage,
    search,
    fileFilters
  );
  const { exporting, handleExport } = useFileMonitoringExport(search, fileFilters);

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;

  const handleResetFilters = useSecurityEdrToolbarReset(resetFilters, setDateRange, setPage);

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'change',
        label: 'Change Type',
        icon: <CreateIcon style={{ fontSize: 14 }} />,
        options: CHANGE_TYPE_OPTIONS,
        value: changeType,
        onChange: (value) => {
          setFilter('changeType', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: SEVERITY_OPTIONS,
        value: severityBand,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
      {
        id: 'ruleLevel',
        label: 'Rule Level',
        options: RULE_LEVEL_OPTIONS,
        value: ruleLevel,
        onChange: (value) => {
          setFilter('ruleLevel', value);
          setPage(0);
        },
      },
      ...(isReseller
        ? [
            {
              id: 'org',
              label: 'Organization',
              variant: 'resellerClient' as const,
              value: organizationId,
              onChange: (value: string) => {
                setFilter('organizationId', value);
                setFilter('wazuhAgentId', '');
                setPage(0);
              },
            },
          ]
        : []),
      {
        id: 'endpoint',
        label: 'Endpoint',
        variant: 'searchableEndpoint' as const,
        pageOrganizationId: organizationId || undefined,
        value: wazuhAgentId,
        onChange: (value: string) => {
          setFilter('wazuhAgentId', value);
          setPage(0);
        },
      },
      {
        id: 'date',
        label: 'Date Range',
        icon: <CalendarTodayIcon style={{ fontSize: 14 }} />,
        options: DATE_RANGE_OPTIONS,
        value: dateRange,
        onChange: (value) => {
          setDateRange(value as WazuhDateRangeOption);
          setPage(0);
        },
      },
    ],
    [
      changeType,
      severityBand,
      ruleLevel,
      wazuhAgentId,
      organizationId,
      dateRange,
      isReseller,
      setDateRange,
      setFilter,
    ]
  );

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'timestamp',
        label: 'Time',
        width: '10%',
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhFimEventItem).timestamp} />
        ),
      },
      {
        id: 'filePath',
        label: 'File Path',
        width: '18%',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhFimEventItem).filePath || '—'}
            style={{ ...edrMonoStyle, fontWeight: 500 }}
            maxWidth={220}
          />
        ),
      },
      {
        id: 'changeType',
        label: 'Change Type',
        width: '10%',
        render: (row) => <ChangeTag value={(row as unknown as WazuhFimEventItem).changeType} />,
      },
      {
        id: 'endpoint',
        label: 'Endpoint',
        width: '12%',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhFimEventItem).endpoint || '—'}
            style={edrMonoStyle}
            maxWidth={140}
          />
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        width: '12%',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhFimEventItem).organization || '—'}
            style={edrMutedStyle}
            maxWidth={140}
          />
        ),
      },
      {
        id: 'user',
        label: 'User',
        width: '8%',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhFimEventItem).user || '—'}
            style={edrMonoStyle}
            maxWidth={100}
          />
        ),
      },
      {
        id: 'severity',
        label: 'Severity',
        width: '10%',
        render: (row) => {
          const item = row as unknown as WazuhFimEventItem;
          return (
            <EdrSeverityPill
              level={fimSeverityToLevel(item.severity)}
              label={toFimTitle(item.severity) || '—'}
            />
          );
        },
      },
      {
        id: 'ruleLevel',
        label: 'Rule Level',
        width: '8%',
        render: (row) => {
          const level = (row as unknown as WazuhFimEventItem).rule?.level;
          return (
            <span style={edrMutedStyle}>
              {typeof level === 'number' ? String(level) : '—'}
            </span>
          );
        },
      },
      {
        id: 'ruleDetail',
        label: 'Rule Detail',
        width: '12%',
        render: (row) => (
          <EdrTruncatedText
            value={formatFimRuleDetail((row as unknown as WazuhFimEventItem).rule)}
            style={edrMutedStyle}
            maxWidth={180}
          />
        ),
      },
    ],
    []
  );

  const monitoredFilesEndpointHint = summary?.monitoredFiles?.endpointCount
    ? `Across ${formatNumber(summary.monitoredFiles.endpointCount)} endpoints`
    : 'Across all monitored endpoints';

  const breakdown = summary?.changeTypeBreakdown;
  const changesTotal = breakdown
    ? (breakdown.added ?? 0) + (breakdown.modified ?? 0) + (breakdown.deleted ?? 0)
    : null;

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — File Monitoring')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="File Monitoring"
          subtitle="Track unauthorized file changes, deletions, and modifications."
          actions={(
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          )}
        />
        {/* Create Monitoring Rule - no backend API yet */}

        <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <EdrKpiCard
            label="Monitored Files"
            value={formatNumber(summary?.monitoredFiles?.total)}
            icon={<FolderIcon style={{ fontSize: 18 }} />}
          >
            {monitoredFilesEndpointHint}
          </EdrKpiCard>
          <EdrKpiCard
            label="File Changes Today"
            value={formatNumber(summary?.fileChangesToday?.total)}
            icon={<RepeatIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.brand500}
          >
            Tracked in the last 24 hours
          </EdrKpiCard>
          <EdrKpiCard
            label="Changes"
            value={formatNumber(changesTotal)}
            icon={<WarningIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.high}
          >
            <EdrStatusPill tone="brand" label={`${formatNumber(breakdown?.added)} Added`} dot={false} />
            <EdrStatusPill
              tone="medium"
              label={`${formatNumber(breakdown?.modified)} Modified`}
              dot={false}
            />
            <EdrStatusPill
              tone="critical"
              label={`${formatNumber(breakdown?.deleted)} Deleted`}
              dot={false}
            />
          </EdrKpiCard>
          <EdrKpiCard
            label="Critical File Alerts"
            value={formatNumber(summary?.criticalFileAlerts?.total)}
            icon={<ShieldIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
            accent={EDR_COLORS.critical}
          >
            Protected system files
          </EdrKpiCard>
        </Box>

        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(v) => {
                setSearchInput(v);
                setPage(0);
              }}
              searchPlaceholder="Search Files…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onRowClick={(row) => openFimEventDetail(row as unknown as WazuhFimEventItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
      </Box>
    </>
  );
};

export default FileIntegrityMonitoring;

